import { useCallback } from "react";

import { useNavigate } from "react-router-dom";

import useSectionScroll, {
  UseSectionScrollArgs,
} from "@app/analytics/hooks/domHelpers/useSectionScroll";
import { useMeetingReportContext } from "@app/analytics/MeetingReport/providers/MeetingReportContext";
import { withQueryParams } from "@app/common/utils";

import useMeetingReportInitialLoading from "./useMeetingReportInitialLoading";

const SCROLL_THRESHOLD_OFFSET = 90; // match the section scrollMarginTop value

type UseMeetingReportSectionScroll<T extends string> = Omit<
  UseSectionScrollArgs<T>,
  "scrollContainer" | "autoSelectThreshold"
>;

const useMeetingReportSectionScroll = <T extends string>(
  args: UseMeetingReportSectionScroll<T>
) => {
  const navigate = useNavigate();
  const { mainScrollContainer } = useMeetingReportContext();

  const { isInitialLoading } = useMeetingReportInitialLoading();

  const handleSectionChange = useCallback((section: T) => {
    navigate(
      withQueryParams(
        window.location.pathname,
        { section },
        window.location.search
      ),
      { replace: true }
    );
  }, []);

  return useSectionScroll<T>({
    ...args,
    enabled: (args.enabled ?? true) && !isInitialLoading,
    autoSelectThreshold: SCROLL_THRESHOLD_OFFSET,
    onSectionChange: handleSectionChange,
    scrollContainer: mainScrollContainer,
  });
};

export default useMeetingReportSectionScroll;
